import clsx from "clsx";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";

import type { Campaign } from "../../api/generated";
import type { CampaignPickerProps } from "../types";

import {
  BaseAlertTriangleIcon,
  CheckPaddedIcon,
  CrossFieldIcon,
  PlusIcon,
  SearchIcon,
} from "../../Icons";
import { ResponsiveSpinner } from "../../ResponsiveSpinner";
import { Typo } from "../../Typo";
import { CampaignPickerTrigger } from "../CampaignPickerTrigger";
import { useCampaignPickerController } from "../hooks/useCampaignPickerController";
import { useCampaignTranslations } from "../hooks/useCampaignTranslations";

const MAX_DROPDOWN_HEIGHT = 384;
const MIN_DROPDOWN_HEIGHT = 160;
const DROPDOWN_CHROME_HEIGHT = 88;
const DROPDOWN_GAP = 8;
const VIEWPORT_MARGIN = 16;

export const CampaignPickerDesktop = ({
  id,
  label,
  subLabel,
  value,
  campaigns,
  onSelect,
  onCreate,
  onCreateSuccess,
  createError,
  disabled = false,
  translations: translationsProp,
  className,
  search,
  onSearchChange,
  isSearchLoading = false,
  isLoading = false,
  hasNextPage = false,
  onLoadMore,
  isLoadingMore = false,
  maxLength,
}: CampaignPickerProps) => {
  // Called unconditionally (rules of hooks); discarded when translationsProp overrides it.
  const defaultTranslations = useCampaignTranslations();
  const translations = translationsProp ?? defaultTranslations;

  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const errorId = `${fieldId}-error`;

  const resolvedLabel = label ?? translations.label;
  const resolvedSubLabel = subLabel ?? translations.subLabel;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLInputElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [placement, setPlacement] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
  }>({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: MAX_DROPDOWN_HEIGHT,
  });
  const [placementReady, setPlacementReady] = useState(false);

  const closeDropdown = () => {
    setOpen(false);
    setHighlightedIndex(-1);
    setPlacementReady(false);
  };

  const {
    query,
    trimmedQuery,
    filteredCampaigns,
    showCreateRow,
    isCreating,
    isMasked,
    handleQueryChange: controllerHandleQueryChange,
    resetQuery,
    handleSelect,
    handleCreate,
    handleResultsScroll,
  } = useCampaignPickerController({
    campaigns,
    onSelect,
    onCreate,
    onCreateSuccess,
    search,
    onSearchChange,
    isSearchLoading,
    isLoading,
    hasNextPage,
    onLoadMore,
    onCommitted: closeDropdown,
    maxLength,
  });

  const maskAriaLabel =
    !isLoading && isSearchLoading ? translations.searchLoadingLabel : undefined;

  const options: Campaign[] = filteredCampaigns;
  const effectiveValue =
    value ?? campaigns.find((campaign) => campaign.isDefault) ?? null;

  const openDropdown = () => {
    if (disabled) return;
    setOpen(true);
    setHighlightedIndex(-1);
    resetQuery();
  };

  const closeDropdownAndRestoreFocus = () => {
    closeDropdown();
    triggerRef.current?.focus();
  };

  const toggleDropdown = () => {
    if (open) closeDropdown();
    else openDropdown();
  };

  useLayoutEffect(() => {
    if (!open) return;

    const updatePlacement = () => {
      const field = triggerRef.current;
      if (!field) return;
      const rect = field.getBoundingClientRect();

      const spaceBelow =
        window.innerHeight - rect.bottom - DROPDOWN_GAP - VIEWPORT_MARGIN;
      const spaceAbove = rect.top - DROPDOWN_GAP - VIEWPORT_MARGIN;
      const openDownwards =
        spaceBelow >= MIN_DROPDOWN_HEIGHT || spaceBelow >= spaceAbove;
      const available = Math.max(
        openDownwards ? spaceBelow : spaceAbove,
        MIN_DROPDOWN_HEIGHT,
      );
      const maxHeight = Math.min(MAX_DROPDOWN_HEIGHT, available);

      setPlacement({
        top: openDownwards ? rect.bottom + DROPDOWN_GAP : undefined,
        bottom: openDownwards
          ? undefined
          : window.innerHeight - rect.top + DROPDOWN_GAP,
        left: rect.left,
        width: rect.width,
        maxHeight,
      });
      setPlacementReady(true);
    };

    const frameId = requestAnimationFrame(updatePlacement);
    window.addEventListener("resize", updatePlacement);
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", updatePlacement);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const field = containerRef.current;
    if (!field) return;

    const scrollableAncestors: HTMLElement[] = [];
    for (
      let parent = field.parentElement;
      parent;
      parent = parent.parentElement
    ) {
      const { overflowY, overflowX } = getComputedStyle(parent);
      if (/(auto|scroll)/.test(overflowY) || /(auto|scroll)/.test(overflowX)) {
        scrollableAncestors.push(parent);
      }
    }

    const handleScroll = () => closeDropdown();
    scrollableAncestors.forEach((el) =>
      el.addEventListener("scroll", handleScroll),
    );
    return () =>
      scrollableAncestors.forEach((el) =>
        el.removeEventListener("scroll", handleScroll),
      );
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open && placementReady) searchInputRef.current?.focus();
  }, [open, placementReady]);

  const handleQueryChange = (nextQuery: string) => {
    controllerHandleQueryChange(nextQuery);
    setHighlightedIndex(-1);
  };

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "ArrowDown") {
      if (isMasked) return;
      event.preventDefault();
      if (options.length === 0) return;
      setHighlightedIndex((index) => (index + 1) % options.length);
    } else if (event.key === "ArrowUp") {
      if (isMasked) return;
      event.preventDefault();
      if (options.length === 0) return;
      setHighlightedIndex((index) =>
        index <= 0 ? options.length - 1 : index - 1,
      );
    } else if (event.key === "Enter") {
      if (
        !isMasked &&
        highlightedIndex >= 0 &&
        highlightedIndex < options.length
      ) {
        event.preventDefault();
        handleSelect(options[highlightedIndex]);
      }
    } else if (event.key === "Escape") {
      closeDropdownAndRestoreFocus();
    }
  };

  return (
    <div
      className={clsx("relative flex w-full flex-col gap-y-2", className)}
      ref={containerRef}
    >
      <CampaignPickerTrigger
        id={fieldId}
        label={resolvedLabel}
        subLabel={resolvedSubLabel}
        value={effectiveValue}
        placeholder={translations.placeholder}
        open={open}
        disabled={disabled}
        error={createError}
        errorId={errorId}
        onActivate={toggleDropdown}
        onArrowDown={openDropdown}
        inputRef={triggerRef}
      />

      {open && (
        <div
          data-testid="campaign-picker-dropdown"
          className="fixed z-floating overflow-hidden rounded-lg border border-stroke-primary bg-surface-primary shadow-dropdown"
          style={{
            top: placement.top,
            bottom: placement.bottom,
            left: placement.left,
            width: placement.width,
            visibility: placementReady ? "visible" : "hidden",
          }}
        >
          <div className="px-4 pt-4">
            <div className="flex h-10 items-center gap-2 rounded-md bg-surface-tonal-default p-2 focus-within:ring-2 focus-within:ring-stroke-focused">
              <SearchIcon className="h-4 w-4 shrink-0 text-icon-tertiary" />
              <input
                ref={searchInputRef}
                type="text"
                role="searchbox"
                aria-label="Search campaigns"
                value={query}
                onChange={(event) => handleQueryChange(event.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder={translations.searchPlaceholder}
                maxLength={maxLength}
                className="typo-body-sm placeholder:typo-body-sm min-w-0 flex-1 bg-transparent text-text-primary placeholder-text-tertiary focus:outline-none"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => handleQueryChange("")}
                  className="flex shrink-0 items-center text-icon-secondary hover:text-icon-primary focus:outline-none"
                  aria-label="Clear search"
                >
                  <CrossFieldIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-4 p-4">
            {isMasked ? (
              <div className="flex items-center justify-center p-4">
                <ResponsiveSpinner
                  height={20}
                  width={20}
                  role="status"
                  aria-label={maskAriaLabel}
                  color="text-icon-brand-default"
                />
              </div>
            ) : (
              <>
                <div
                  data-testid="campaign-picker-results"
                  className="overflow-y-auto"
                  style={{
                    maxHeight: Math.max(
                      placement.maxHeight - DROPDOWN_CHROME_HEIGHT,
                      MIN_DROPDOWN_HEIGHT - DROPDOWN_CHROME_HEIGHT,
                    ),
                  }}
                  onScroll={handleResultsScroll}
                >
                  {options.length > 0 && (
                    <div
                      role="listbox"
                      aria-label={resolvedLabel}
                      aria-busy={isLoadingMore}
                    >
                      {options.map((option, index) => {
                        const isSelected = effectiveValue?.id === option.id;
                        const isHighlighted = index === highlightedIndex;
                        return (
                          <div
                            key={option.id}
                            role="option"
                            data-testid={`campaign_picker_desktop_campaign_option_${option.id}`}
                            aria-selected={isSelected}
                            className={clsx(
                              "flex cursor-pointer items-center justify-between gap-2 rounded-md p-2 hover:bg-surface-tonal-default",
                              isSelected && "bg-state-selected",
                              isHighlighted &&
                                !isSelected &&
                                "bg-surface-tonal-default",
                            )}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            onClick={() => handleSelect(option)}
                          >
                            <Typo
                              element="p"
                              type="bodySM"
                              textColor={
                                isSelected ? "text-text-selected" : undefined
                              }
                              className="min-w-0 flex-1 break-words"
                            >
                              {option.name}
                            </Typo>
                            {isSelected && (
                              <CheckPaddedIcon
                                className="h-6 w-6 shrink-0 text-icon-selected"
                                aria-hidden="true"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {trimmedQuery === "" && campaigns.length === 0 && (
                    <div className="p-2">
                      <Typo
                        element="p"
                        type="bodySM"
                        textColor="text-text-secondary"
                      >
                        {translations.noCampaignsLabel}
                      </Typo>
                    </div>
                  )}
                  {showCreateRow && !isCreating && (
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label={translations.createOptionLabel(trimmedQuery)}
                      className="flex cursor-pointer items-center gap-2 rounded-md p-2 hover:bg-surface-tonal-default"
                      onClick={() => void handleCreate(trimmedQuery)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          void handleCreate(trimmedQuery);
                        }
                      }}
                    >
                      <PlusIcon className="h-4 w-4 shrink-0 text-icon-brand-default" />
                      <Typo
                        element="p"
                        type="labelSM"
                        textColor="text-text-brand-default"
                        className="min-w-0 flex-1 break-words"
                      >
                        {translations.createOptionLabel(trimmedQuery)}
                      </Typo>
                    </div>
                  )}
                  {(isLoadingMore || isCreating) && (
                    <div
                      role="status"
                      aria-label={
                        isCreating ? translations.creatingLabel : undefined
                      }
                      className="flex items-center justify-center gap-2 p-2"
                    >
                      <ResponsiveSpinner
                        height={16}
                        width={16}
                        color="text-icon-brand-default"
                      />
                    </div>
                  )}
                </div>
                {createError && (
                  <div id={errorId} role="alert" className="flex gap-1">
                    <div className="flex items-center py-px text-icon-error">
                      <BaseAlertTriangleIcon className="h-4 w-4" />
                    </div>
                    <Typo element="p" type="bodyXS" textColor="text-text-error">
                      {createError}
                    </Typo>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
